(ns frontend.ui.component.lp-portal.report.component.waterfall-chart.controller
  (:require
   [clojure.string :as string]
   [common.util :as cu]
   [frontend.ui.component.chart.utils :as chart-utils]
   [frontend.ui.page.lp-portal.report.util :as report-utils]
   [frontend.ui.style.theme.core :as theme]
   ["highcharts" :as highcharts]))

(def max-waterfall-label-line-length 16)
(def base-waterfall-margin-bottom 60)
(def extra-waterfall-line-height 14)

(defn- split-long-token
  [token]
  (if (<= (count token) max-waterfall-label-line-length)
    [token]
    (->> token
         (partition-all max-waterfall-label-line-length)
         (map (fn [chars]
                (apply str chars)))
         vec)))

(defn- wrap-waterfall-label-lines
  [label]
  (let [normalized-label (-> (or label "")
                             str
                             (string/replace #"\s+" " ")
                             string/trim)
        words (->> (string/split normalized-label #" ")
                   (remove cu/blank-value?)
                   (mapcat split-long-token))]
    (if (empty? words)
      [""]
      (reduce
        (fn [lines word]
          (let [current-line (or (peek lines) "")
                candidate-line (if (cu/blank-value? current-line)
                                 word
                                 (str current-line " " word))]
            (if (<= (count candidate-line) max-waterfall-label-line-length)
              (conj (pop lines) candidate-line)
              (conj lines word))))
        [""]
        words))))

(defn- wrapped-waterfall-label-html
  [label]
  (->> (wrap-waterfall-label-lines label)
       (string/join "<br/>")))

(defn- waterfall-margin-bottom
  [max-label-lines]
  (+ base-waterfall-margin-bottom
     (* (max 0 (- max-label-lines 2))
        extra-waterfall-line-height)))

(defn chart-config
  [chart-data _report-data]
  (let [dt (->>
            (mapv (fn [label value color-code label-multiplier label-decimal tooltip-multiplier tooltip-decimal]
                    {:name label
                     :y value
                     :color color-code
                     :label-multiplier label-multiplier
                     :label-decimal label-decimal
                     :tooltip-multiplier tooltip-multiplier
                     :tooltip-decimal tooltip-decimal})
                  (:labels (:columns chart-data))
                  (:value (:columns chart-data))
                  (:color-code (:columns chart-data))
                  (:label-multiplier (:columns chart-data))
                  (:label-decimals (:columns chart-data))
                  (:tooltip-multiplier (:columns chart-data))
                  (:tooltip-decimals (:columns chart-data)))
            (doall))
        y-axis-config (let [y-axis (:y-axis-configs chart-data)
                            title (:y-axis-title y-axis)
                            title-color (:y-axis-title-color y-axis)
                            y-axis-side (:y-axis-side y-axis)
                            y-axis-multiplier (:y-axis-multiplier y-axis)
                            y-axis-decimals (:y-axis-decimals y-axis)]
                        {:title     {:text     title
                                     :align    "high"
                                     :offset   0
                                     :rotation 0
                                     :y        -10
                                     :style    {:color title-color}}
                         :lineWidth 1
                         :lineColor (theme/color [:lp-report :chart :y-axis :line])
                         :labels    {:formatter (fn [label]
                                                  (report-utils/->float-x-decimals
                                                   (/ (.-value label) y-axis-multiplier)
                                                   y-axis-decimals))}
                         :opposite  (case y-axis-side
                                      "right" true
                                      "left" false
                                      true)
                         :min       (let [min-sum (->> (range 0 (count dt))
                                                       (map (fn [idx]
                                                              (->> dt
                                                                   (take idx)
                                                                   (map (fn [data-point]
                                                                          (get data-point :y 0)))
                                                                   (apply +))))
                                                       (apply min))]
                                      (if (neg? min-sum)
                                        min-sum
                                        0))})]
    {:chart       {:type      "waterfall"
                   :marginTop 40}
     :tooltip     {:outside         true
                   :useHtml         true
                   :backgroundColor (theme/color [:page-bg :bg])
                   :style           {:opacity         1
                                     :backgroundColor (theme/color [:page-bg :bg])}
                   :enabled         true}
     :plotOptions {:series {:colorByPoint true
                            :borderWidth  0
                            :states       {:hover {:color (theme/color [:lp-report :chart :series-hover :bg])}}
                            :tooltip      {:pointFormatter (fn []
                                                             (this-as
                                                              js-data-point
                                                              (let [point (js->clj js-data-point
                                                                                   :keywordize-keys
                                                                                   true)
                                                                    index (aget point "category")
                                                                    multiplier (->> (nth dt index)
                                                                                    (:label-multiplier))
                                                                    decimal (->> (nth dt index)
                                                                                 (:label-decimal))
                                                                    suffix (case multiplier
                                                                             1 ""
                                                                             1000 "k"
                                                                             1000000 "m"
                                                                             "")]
                                                                (chart-utils/float-tooltip-formatter-dynamic-decimals
                                                                 js-data-point
                                                                 suffix
                                                                 multiplier
                                                                 decimal))))}
                            ;;TODO maybe refactor these, much duplication now
                            :dataLabels   {:enabled   true
                                           :inside    false
                                           :formatter (fn []
                                                        (this-as
                                                         js-data-point
                                                         (let [point (js->clj js-data-point
                                                                              :keywordize-keys
                                                                              true)
                                                               index (-> point
                                                                         :point
                                                                         (aget "category"))
                                                               multiplier (->> (nth dt index)
                                                                               (:label-multiplier))
                                                               decimal (->> (nth dt index)
                                                                            (:label-decimal))
                                                               suffix (case multiplier
                                                                        1 ""
                                                                        1000 "k"
                                                                        1000000 "m"
                                                                        "")]
                                                           (chart-utils/generic-label-formatter
                                                            js-data-point
                                                            multiplier
                                                            decimal
                                                            (if (= multiplier
                                                                   (:y-axis-multiplier
                                                                    (:y-axis-configs chart-data)))
                                                              ""
                                                              suffix)))))}}}

     :legend      {:enabled false}
     :xAxis       {:type      "category"
                   :lineColor (theme/color [:lp-report :chart :x-axis :line])}
     :yAxis       y-axis-config
     :series      [{:data (->>
                           dt
                           (map-indexed (fn [idx data-item]
                                          (merge data-item
                                                 (when (= (inc idx) (count dt))
                                                   {:isSum true})
                                                 (cond
                                                   (not (cu/blank-value? (:color data-item)))
                                                   {:color (:color data-item)}
                                                   (= (inc idx) (count dt))
                                                   {:color (theme/color [:lp-report :chart :waterfall :sum])}
                                                   (zero? idx)
                                                   {:color (theme/color [:lp-report :chart :waterfall :sum])}
                                                   (neg? (:y data-item))
                                                   {:color (theme/color [:lp-report :chart :waterfall :down])}
                                                   (pos? (:y data-item))
                                                   {:color (theme/color [:lp-report :chart :waterfall :up])}))))
                           (doall))}]}))

(defn chart-config-capman
  [chart-data _report-data]
  (let [dt (->>
            (mapv (fn [label value color-code label-multiplier label-decimal tooltip-multiplier tooltip-decimal]
                    {:name label
                     :y value
                     :color color-code
                     :label-multiplier label-multiplier
                     :label-decimal label-decimal
                     :tooltip-multiplier tooltip-multiplier
                     :tooltip-decimal tooltip-decimal})
                  (:labels (:columns chart-data))
                  (:value (:columns chart-data))
                  (:color-code (:columns chart-data))
                  (:label-multiplier (:columns chart-data))
                  (:label-decimals (:columns chart-data))
                  (:tooltip-multiplier (:columns chart-data))
                  (:tooltip-decimals (:columns chart-data)))
            (doall))
        max-label-lines (->> dt
                             (map :name)
                             (map (fn [name]
                                    (count (wrap-waterfall-label-lines name))))
                             (apply max 1))
        y-axis-config (let [y-axis (:y-axis-configs chart-data)
                            title (:y-axis-title y-axis)
                            title-color (:y-axis-title-color y-axis)
                            y-axis-side (:y-axis-side y-axis)
                            y-axis-multiplier (:y-axis-multiplier y-axis)
                            y-axis-decimals (:y-axis-decimals y-axis)]
                        {:title          {:text     title
                                          :align    "middle"
                                          :x 0
                                          :y 0
                                          :style    {:color title-color
                                                     :fontSize "0.8em"}}
                         :lineWidth 0
                         :lineColor (theme/color [:lp-report :chart :y-axis :line])
                         :gridLineColor "#d7d7d7"
                         :labels    {:formatter (fn []
                                                  (this-as js-data-point
                                                           (.numberFormat highcharts
                                                                          (/ (.-value js-data-point) y-axis-multiplier)
                                                                          y-axis-decimals
                                                                          "."
                                                                          ",")))}
                         :opposite  (case y-axis-side
                                      "right" true
                                      "left" false
                                      true)
                         :min       (let [min-sum (->> (range 0 (count dt))
                                                       (map (fn [idx]
                                                              (->> dt
                                                                   (take idx)
                                                                   (map (fn [data-point]
                                                                          (get data-point :y 0)))
                                                                   (apply +))))
                                                       (apply min))]
                                      (if (neg? min-sum)
                                        min-sum
                                        0))})]
    {:chart       {:type      "waterfall"
                   :marginTop 40
                   :marginBottom (waterfall-margin-bottom max-label-lines)
                   :style {:fontSize "16px"}}
     :tooltip     {:outside         true
                   :useHtml         true
                   :backgroundColor (theme/color [:page-bg :bg])
                   :style           {:opacity         1
                                     :backgroundColor (theme/color [:page-bg :bg])}
                   :enabled         true}
     :plotOptions {:series {:colorByPoint true
                            :borderWidth  0
                            :states       {:hover {:color (theme/color [:lp-report :chart :series-hover :bg])}}
                            :tooltip      {:pointFormatter (fn []
                                                             (this-as
                                                              js-data-point
                                                              (let [point (js->clj js-data-point
                                                                                   :keywordize-keys
                                                                                   true)
                                                                    index (aget point "category")
                                                                    multiplier (->> (nth dt index)
                                                                                    (:label-multiplier))
                                                                    decimal (->> (nth dt index)
                                                                                 (:label-decimal))
                                                                    suffix (case multiplier
                                                                             1 ""
                                                                             1000 "k"
                                                                             1000000 "m"
                                                                             "")]
                                                                (chart-utils/float-tooltip-formatter-dynamic-decimals
                                                                 js-data-point
                                                                 suffix
                                                                 multiplier
                                                                 decimal))))}
                            ;;TODO maybe refactor these, much duplication now
                            :dataLabels   {:enabled   true
                                           :inside    false
                                           :style {:fontSize    "0.8em"
                                                   :fontWeight "normal"
                                                   :color       "contrast"
                                                   :textOutline false}
                                           :formatter (fn []
                                                        (this-as
                                                         js-data-point
                                                         (let [point (js->clj js-data-point
                                                                              :keywordize-keys
                                                                              true)
                                                               index (-> point
                                                                         :point
                                                                         (aget "category"))
                                                               multiplier (->> (nth dt index)
                                                                               (:label-multiplier))
                                                               decimal (->> (nth dt index)
                                                                            (:label-decimal))
                                                               suffix (case multiplier
                                                                        1 ""
                                                                        1000 "k"
                                                                        1000000 "m"
                                                                        "")]
                                                           (chart-utils/generic-label-formatter
                                                            js-data-point
                                                            multiplier
                                                            decimal
                                                            (if (= multiplier
                                                                   (:y-axis-multiplier
                                                                    (:y-axis-configs chart-data)))
                                                              ""
                                                              suffix)))))}}}

     :legend      {:enabled false}
     :xAxis       {:type      "category"
                   :lineColor (theme/color [:lp-report :chart :x-axis :line])
                   :labels    {:useHTML false
                               :style   {:textOverflow "none"}
                               :formatter (fn []
                                            (this-as js-data-point
                                                     (wrapped-waterfall-label-html
                                                       (.-value js-data-point))))}}
     :yAxis       y-axis-config
     :series      [{:data (->>
                           dt
                           (map-indexed (fn [idx data-item]
                                          (merge data-item
                                                 (when (= (inc idx) (count dt))
                                                   {:isSum true})
                                                 (cond
                                                   (not (cu/blank-value? (:color data-item)))
                                                   {:color (:color data-item)}
                                                   (= (inc idx) (count dt))
                                                   {:color (theme/color [:lp-report :chart :waterfall :sum])}
                                                   (zero? idx)
                                                   {:color (theme/color [:lp-report :chart :waterfall :sum])}
                                                   (neg? (:y data-item))
                                                   {:color (theme/color [:lp-report :chart :waterfall :down])}
                                                   (pos? (:y data-item))
                                                   {:color (theme/color [:lp-report :chart :waterfall :up])}))))
                           (doall))}]}))
